import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  policyChargesService,
  type CreateManualChargePayload,
  type PolicyCharge,
  type PolicyChargeAttempt,
  type PolicyChargeRefund,
  type PolicyRecurrenceRefreshResult,
  type RefundPolicyChargesResult,
} from '../../../services/policy-charges';

const CHARGES_KEY = 'policy-charges';
const ATTEMPTS_KEY = 'policy-charge-attempts';
const REFUNDS_KEY = 'policy-charge-refunds';

/**
 * Cobranças por link de pagamento da apólice.
 *
 * Produto que não usa cobrança por link devolve lista VAZIA com sucesso, e a aba
 * usa esse vazio para não renderizar as colunas de link.
 *
 * Falha de rede ou de permissão LANÇA, em vez de virar lista vazia. Parecem a
 * mesma coisa na tela e não são: um 403 na leitura deixava a aba idêntica à de
 * um produto sem gateway — sem link, sem forma de pagamento, sem erro — e foi
 * exatamente assim que uma permissão inexistente passou despercebida.
 */
export function usePolicyCharges(policyId: string | undefined, enabled = true) {
  return useQuery<PolicyCharge[], Error>({
    queryKey: [CHARGES_KEY, policyId],
    queryFn: async () => {
      if (!policyId) return [];
      const result = await policyChargesService.getCharges(policyId);
      if (!result.success) {
        throw new Error(
          result.error || 'Erro ao carregar as cobranças da apólice'
        );
      }
      return result.data ?? [];
    },
    enabled: !!policyId && enabled,
    // Curto de propósito: o status muda por webhook do lado de fora, então a
    // tela precisa refletir o pagamento sem o usuário ter que recarregar.
    staleTime: 30 * 1000,
  });
}

/** Histórico de tentativas — carregado sob demanda, ao expandir a parcela. */
export function usePolicyChargeAttempts(
  policyId: string | undefined,
  installmentNumber: number | undefined,
  enabled = true
) {
  return useQuery<PolicyChargeAttempt[], Error>({
    queryKey: [ATTEMPTS_KEY, policyId, installmentNumber ?? 'all'],
    queryFn: async () => {
      if (!policyId) return [];
      const result = await policyChargesService.getAttempts(
        policyId,
        installmentNumber
      );
      if (!result.success) {
        throw new Error(
          result.error || 'Erro ao carregar o histórico de tentativas'
        );
      }
      return result.data ?? [];
    },
    enabled: !!policyId && enabled,
    staleTime: 30 * 1000,
  });
}

export function useRegeneratePolicyCharge(policyId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation<PolicyCharge[], Error, number | undefined>({
    mutationFn: async installmentNumber => {
      if (!policyId) throw new Error('Apólice não informada');
      const result = await policyChargesService.regenerate(
        policyId,
        installmentNumber
      );
      if (!result.success || !result.data) {
        throw new Error(result.error || 'Erro ao gerar o link de pagamento');
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CHARGES_KEY, policyId] });
      queryClient.invalidateQueries({ queryKey: [ATTEMPTS_KEY, policyId] });
    },
  });
}

export function useCreateManualPolicyCharge(policyId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation<PolicyCharge, Error, CreateManualChargePayload>({
    mutationFn: async payload => {
      if (!policyId) throw new Error('Apólice não informada');
      const result = await policyChargesService.createManual(policyId, payload);
      if (!result.success || !result.data) {
        throw new Error(result.error || 'Erro ao criar a cobrança manual');
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CHARGES_KEY, policyId] });
      queryClient.invalidateQueries({ queryKey: [ATTEMPTS_KEY, policyId] });
    },
  });
}

/** "Reenviar ao segurado": manda de novo o link ativo, sem criar outro. */
export function useResendPolicyChargeNotification(
  policyId: string | undefined
) {
  const queryClient = useQueryClient();

  return useMutation<PolicyCharge, Error, number>({
    mutationFn: async installmentNumber => {
      if (!policyId) throw new Error('Apólice não informada');
      const result = await policyChargesService.resendNotification(
        policyId,
        installmentNumber
      );
      if (!result.success || !result.data) {
        throw new Error(result.error || 'Erro ao reenviar o link ao segurado');
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CHARGES_KEY, policyId] });
      queryClient.invalidateQueries({ queryKey: [ATTEMPTS_KEY, policyId] });
    },
  });
}

/** Cancela o PIX Automático da apólice — o banco para de debitar. */
export function useCancelPolicyRecurrence(policyId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation<PolicyCharge[], Error, string>({
    mutationFn: async reason => {
      if (!policyId) throw new Error('Apólice não informada');
      const result = await policyChargesService.cancelRecurrence(
        policyId,
        reason
      );
      if (!result.success || !result.data) {
        throw new Error(result.error || 'Erro ao cancelar o PIX Automático');
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CHARGES_KEY, policyId] });
      queryClient.invalidateQueries({ queryKey: [ATTEMPTS_KEY, policyId] });
    },
  });
}

/** "Verificar no banco": confere o PIX Automático direto na Autra. */
export function useRefreshPolicyRecurrence(policyId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation<PolicyRecurrenceRefreshResult, Error, void>({
    mutationFn: async () => {
      if (!policyId) throw new Error('Apólice não informada');
      const result = await policyChargesService.refreshRecurrence(policyId);
      if (!result.success || !result.data) {
        throw new Error(
          result.error || 'Erro ao verificar o PIX Automático no banco'
        );
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CHARGES_KEY, policyId] });
      queryClient.invalidateQueries({ queryKey: [ATTEMPTS_KEY, policyId] });
    },
  });
}

/** Estornos no cartão da apólice. */
export function usePolicyChargeRefunds(
  policyId: string | undefined,
  enabled = true
) {
  return useQuery<PolicyChargeRefund[], Error>({
    queryKey: [REFUNDS_KEY, policyId],
    queryFn: async () => {
      if (!policyId) return [];
      const result = await policyChargesService.getRefunds(policyId);
      if (!result.success) {
        throw new Error(result.error || 'Erro ao carregar os estornos');
      }
      return result.data ?? [];
    },
    enabled: Boolean(policyId) && enabled,
  });
}

/** "Estornar": uma parcela (`installmentNumber`) ou, sem ela, a apólice inteira. */
export function useRefundPolicyCharges(policyId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation<
    RefundPolicyChargesResult,
    Error,
    { installmentNumber?: number; reason: string }
  >({
    mutationFn: async payload => {
      if (!policyId) throw new Error('Apólice não informada');
      const result = await policyChargesService.refund(policyId, payload);
      if (!result.success || !result.data) {
        throw new Error(result.error || 'Erro ao estornar');
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CHARGES_KEY, policyId] });
      queryClient.invalidateQueries({ queryKey: [REFUNDS_KEY, policyId] });
      queryClient.invalidateQueries({ queryKey: [ATTEMPTS_KEY, policyId] });
    },
  });
}
